// PolicyIntel UI v2 — personal stock monitoring workspace.
// Saving a stock uses the existing /live-summary/holdings authority. It does
// not assert that the user owns shares or subscribe them to push notifications.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import {
  addLiveSummaryHolding, getLiveSummaryHoldings, getLiveSummaryChanges,
  removeLiveSummaryHolding, searchLiveSummaryHoldings,
  type LiveSummaryHolding, type LiveSummaryHoldingSearchItem,
} from '../api/liveSummaryApi';
import { trackUxEvent } from '../analytics/analytics';

type Raw = Record<string, any>;
type Severity = 'risk' | 'watch' | 'stable' | 'unknown';
type Saved = { ticker:string; name:string; price:number|null; rate:number|null;
  severity:Severity; decision:string; reason:string; next:string; asOf:string; isHolding:boolean };
const C = {bg:'#F6F8FB',white:'#FFFFFF',ink:'#122033',muted:'#607086',line:'#E2E8F0',
  navy:'#10263F',blue:'#2F6BFF',green:'#178A52',amber:'#D58A16',red:'#D84C57',unknown:'#77869A'};
const text = (v:unknown):string => v==null?'':String(v).trim();
const first = (...xs:unknown[]):string => xs.map(text).find(Boolean)||'';
const num = (v:unknown):number|null => v == null || v === '' ? null : Number.isFinite(Number(v))?Number(v):null;
const tickerCode = (v:unknown):string => text(v).match(/^(?:A)?(\d{6})$/)?.[1] || '';
const pct = (v:number|null)=>v===null?'미확인':`${v>0?'+':''}${v.toFixed(2)}%`;
const money = (v:number|null)=>v===null?'가격 확인 중':`${Math.round(v).toLocaleString('ko-KR')}원`;
function classify(row:Raw):Severity {
  const risk=(row.riskAssessment??row.risk_assessment??{}) as Raw;
  const thesis=(row.holdingThesis??{}) as Raw;
  const s=first(risk.state,risk.riskState,thesis.displayState,thesis.coreState).toUpperCase();
  if(['HIGH_RISK','DEFENSIVE','DAMAGED','CRITICAL','REVIEW'].includes(s))return 'risk';
  if(['CAUTION','WATCH','WEAKENED','PRESERVED_CAUTION'].includes(s))return 'watch';
  if(['NORMAL','STABLE','HEALTHY','PRESERVED'].includes(s))return 'stable';
  return 'unknown';
}
function normalize(row:LiveSummaryHolding):Saved|null {
  const r=row as Raw; const ticker=tickerCode(r.ticker);if(!ticker)return null;
  const risk=(r.riskAssessment??{}) as Raw;
  const thesis=(r.holdingThesis??{}) as Raw;
  const decision=(r.actionDecision??{}) as Raw;
  // The holdings endpoint stores user-tracked securities. It does NOT prove
  // actual share ownership; separate flags must explicitly assert ownership.
  const kind=first(r.positionType,r.stockListType,r.listType).toUpperCase();
  const quantity=num(r.portfolioQuantity);
  const isHolding=(quantity!==null&&quantity>0)||r.portfolioPositionAvailable===true||r.isHolding===true||r.isHolding===1||r.owned===true||kind==='HOLDING'||kind==='OWNED';
  return {ticker,name:first(r.name,r.corpName,ticker),price:num(r.price??r.currentPrice),
    rate:num(r.changeRate??r.priceChangePct),severity:classify(r),
    decision:first(thesis.displayLabel,risk.stateLabel,r.status,'판단 확인 필요'),
    reason:first(Array.isArray(thesis.reasons)?thesis.reasons[0]:null,risk.summary,decision.headline,'확인 가능한 판단 근거를 불러오는 중입니다.'),
    next:first(Array.isArray(thesis.recheckConditions)?thesis.recheckConditions[0]:null,Array.isArray(thesis.monitorConditions)?thesis.monitorConditions[0]:null,risk.nextCheck,decision.nextCheck,'검증된 다음 확인 조건이 없습니다.'),
    asOf:first(r.priceAsOf,thesis.basisDate,r.sessionDate,r.lastEventAt,'관측시각 확인 필요'),isHolding};
}
function changeTicker(item:Raw):string {
  const direct=tickerCode(item.ticker??item.stockCode??item.stock_code);if(direct)return direct;
  return first(item.entityKey,item.entity_key).match(/(?:^|\D)(\d{6})(?:\D|$)/)?.[1]||'';
}
export default function WatchWorkspacePage(){
  const navigate=useNavigate();
  const [rows,setRows]=useState<Saved[]>([]);
  const [changes,setChanges]=useState<Raw[]>([]);
  const [loaded,setLoaded]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [feedback,setFeedback]=useState('');
  const [query,setQuery]=useState('');
  const [suggestions,setSuggestions]=useState<LiveSummaryHoldingSearchItem[]>([]);
  const [searchLoading,setSearchLoading]=useState(false);
  const [mutating,setMutating]=useState('');
  const [mode,setMode]=useState<'ALL'|'WATCH'|'HOLDING'>('ALL');
  const requestRef=useRef(0);
  const searchRef=useRef(0);
  const savingRef=useRef('');
  const load=useCallback(async()=>{
    const request=++requestRef.current;
    setLoading(true);setError('');
    const [a,b]=await Promise.allSettled([
      getLiveSummaryHoldings({refresh:true,timeoutMs:12000}),
      getLiveSummaryChanges({limit:100,timeoutMs:7000}),
    ]);
    if(request!==requestRef.current)return;
    if(a.status==='fulfilled'){
      const unique=new Map<string,Saved>();
      for(const r of a.value){const s=normalize(r);if(s)unique.set(s.ticker,s);}
      setRows(Array.from(unique.values()));setLoaded(true);
    }else{setError('감시종목 목록을 확인할 수 없습니다. 서버 연결을 확인하고 다시 시도해 주세요.');}
    if(b.status==='fulfilled')setChanges(Array.isArray(b.value.changes) ? b.value.changes : []);
    else setChanges([]);
    setLoading(false);
  },[]);
  useEffect(()=>{
    trackUxEvent('watch_workspace_view',{screen_name:'watch',source:'ui-v2'});
    void load();
    const refresh=()=>{if(document.visibilityState==='visible')void load();};
    const saved=()=>{void load();};
    document.addEventListener('visibilitychange',refresh);
    window.addEventListener('policy-intel:watchlist-updated',saved);
    const pendingRequests=requestRef;const pendingSearch=searchRef;
    return()=>{pendingRequests.current++;pendingSearch.current++;document.removeEventListener('visibilitychange',refresh);window.removeEventListener('policy-intel:watchlist-updated',saved);};
  },[load]);
  useEffect(()=>{
    const request=++searchRef.current;
    const q=query.trim();
    if(q.length<2){setSuggestions([]);setSearchLoading(false);return;}
    setSearchLoading(true);
    const timer=window.setTimeout(()=>{
      void searchLiveSummaryHoldings(q,{limit:12,timeoutMs:6000}).then(list=>{
        if(request!==searchRef.current)return;
        setSuggestions(list.filter(s=>Boolean(tickerCode(s.ticker))));
      }).catch(()=>{if(request===searchRef.current){setSuggestions([]);setFeedback('종목 검색 연결을 확인할 수 없습니다.');}})
        .finally(()=>{if(request===searchRef.current)setSearchLoading(false);});
    },250);
    return()=>window.clearTimeout(timer);
  },[query]);
  const known=useMemo(()=>new Set(rows.map(s=>s.ticker)),[rows]);
  const trackedChanges=useMemo(()=>{
    const seen=new Set<string>();
    return changes.filter(item=>{
      const ticker=changeTicker(item);if(!known.has(ticker))return false;
      const key=first(item.id,item.changeId,item.eventId,`${ticker}:${first(item.title,item.message)}:${first(item.detectedAt)}`);
      if(seen.has(key))return false;seen.add(key);return true;
    });
  },[known,changes]);
  const priority=useMemo<Record<Severity,number>>(()=>({risk:0,watch:1,unknown:2,stable:3}),[]);
  const visible=useMemo(()=>rows.filter(r=>mode==='ALL'||(mode==='HOLDING'?r.isHolding:!r.isHolding))
    .sort((a,b)=>priority[a.severity]-priority[b.severity]||a.name.localeCompare(b.name,'ko')),[rows,mode,priority]);
  const add=async(ticker:string)=>{
    const code=tickerCode(ticker);if(!code||savingRef.current)return;
    savingRef.current=code;setMutating(code);setFeedback('');
    trackUxEvent('stock_track_click',{ticker:code,stock_code:code,source:'watch-workspace',intent:'WATCHLIST'});
    try{
      const result=await addLiveSummaryHolding(code);
      if(!result.active||tickerCode(result.ticker)!==code)throw new Error('저장 응답 확인 실패');
      // Server read-back is the authority for showing the stock as saved.
      const saved=await getLiveSummaryHoldings({refresh:true,timeoutMs:12000});
      const found=saved.find(x=>tickerCode(x.ticker)===code);
      if(!found)throw new Error('등록 응답은 받았으나 목록 반영을 확인하지 못했습니다. 다시 조회해 주세요.');
      const unique=new Map<string,Saved>();
      for(const raw of saved){const item=normalize(raw);if(item)unique.set(item.ticker,item);}
      setRows(Array.from(unique.values()));setLoaded(true);
      setFeedback(`${first(found.name,code)} 감시종목 등록을 확인했습니다.`);
      const mutationEvent = result.action === 'CREATED'
        ? 'stock_track_created'
        : result.action === 'RESTORED'
          ? 'stock_track_restored'
          : 'stock_track_already_active';
      trackUxEvent(mutationEvent,{ticker:code,stock_code:code,source:'watch-workspace',mutation_action:result.action});
      setQuery('');setSuggestions([]);
      window.dispatchEvent(new Event('policy-intel:watchlist-updated'));
    }catch(e:any){setFeedback(first(e?.message,'감시종목을 저장하지 못했습니다. 다시 시도해 주세요.'));}
    finally{savingRef.current='';setMutating('');}
  };
  const remove=async(ticker:string,name:string)=>{
    if(savingRef.current||!window.confirm(`${name}을(를) 감시종목에서 제외하시겠습니까?`))return;
    savingRef.current=ticker;setMutating(ticker);setFeedback('');
    try{
      await removeLiveSummaryHolding(ticker);
      const saved=await getLiveSummaryHoldings({refresh:true,timeoutMs:12000});
      if(saved.some(s=>tickerCode(s.ticker)===ticker))throw new Error('삭제 여부를 서버에서 확인하지 못했습니다.');
      setRows(saved.map(normalize).filter((x):x is Saved=>x!==null));
      setFeedback(`${name} 감시를 해제했습니다.`);
      trackUxEvent('stock_track_remove_success',{ticker,stock_code:ticker,source:'watch-workspace'});
    }catch(e:any){setFeedback(first(e?.message,'감시 해제를 확인하지 못했습니다.'));}
    finally{savingRef.current='';setMutating('');}
  };
  const shade=(x:Severity)=>x==='risk'?C.red:x==='watch'?C.amber:x==='stable'?C.green:C.unknown;
  return <Box sx={{background:C.bg,minHeight:'calc(100vh - 64px)',color:C.ink,py:{xs:2,md:4}}}>
    <Box sx={{maxWidth:1220,mx:'auto',px:{xs:2,md:4}}}>
      <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{xs:'start',sm:'center'}} gap={2}>
        <Box><Typography sx={{fontSize:12,letterSpacing:'.09em',fontWeight:850,color:C.blue}}>PERSONAL WATCH</Typography>
          <Typography component="h1" sx={{fontSize:{xs:29,md:34},fontWeight:850,letterSpacing:'-.045em',mt:.5}}>내 종목</Typography>
          <Typography sx={{fontSize:14,color:C.muted,mt:.7}}>등록한 종목의 판단 변화와 다음 확인 조건을 한 곳에서 확인합니다.</Typography></Box>
        <Button disabled={loading} onClick={()=>void load()} startIcon={loading?<CircularProgress size={17}/>:<RefreshRoundedIcon/>}
          variant="outlined" sx={{borderColor:C.line,color:C.navy,fontWeight:800,textTransform:'none',borderRadius:2}}>새로 확인</Button>
      </Stack>
      <Box sx={{display:'grid',gridTemplateColumns:{xs:'repeat(2,minmax(0,1fr))',md:'repeat(3,minmax(0,1fr))'},gap:1.5,mt:3}}>
        {([['감시 종목',rows.length],['지금 확인할 위험',rows.filter(x=>x.severity==='risk').length],['최근 변화',trackedChanges.length]] as const).map(([label,value])=><Box key={label} sx={{p:2.5,bgcolor:C.white,border:`1px solid ${C.line}`,borderRadius:3}}><Typography sx={{fontSize:13,color:C.muted}}>{label}</Typography><Typography sx={{fontSize:28,fontWeight:850,mt:1}}>{loaded?value:'—'}</Typography></Box>)}
      </Box>
      <Box sx={{bgcolor:C.white,border:`1px solid ${C.line}`,p:{xs:2,md:3},borderRadius:3,mt:2.5}}>
        <Typography component="h2" sx={{fontSize:20,fontWeight:850}}>관심종목 등록</Typography>
        <Typography sx={{fontSize:13.5,color:C.muted,mt:.7,mb:2}}>종목을 검색한 뒤 등록하면 다음부터 변화와 위험을 확인할 수 있습니다. 주식을 실제 보유했다는 뜻은 아닙니다.</Typography>
        <TextField fullWidth size="medium" placeholder="종목명 또는 6자리 종목코드" value={query}
          onChange={e=>{setQuery(e.target.value);setFeedback('');}} inputProps={{'aria-label':'관심종목 검색'}}
          InputProps={{startAdornment:<InputAdornment position="start"><SearchRoundedIcon sx={{color:C.muted}}/></InputAdornment>,endAdornment:searchLoading?<CircularProgress size={18}/>:null}}
          sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/>
        {query.trim().length>=2 && <Box sx={{mt:1,border:`1px solid ${C.line}`,borderRadius:2,overflow:'hidden'}} role="listbox" aria-label="관심종목 검색 결과">
          {searchLoading&&suggestions.length===0?<Typography sx={{p:2,fontSize:13,color:C.muted}}>검색 중...</Typography>:
          suggestions.length===0?<Typography sx={{p:2,fontSize:13,color:C.muted}}>일치하는 종목이 없거나 검색 결과를 확인하지 못했습니다.</Typography>:
          suggestions.map(s=>{const code=tickerCode(s.ticker),exists=known.has(code)||s.alreadyAdded===true;
            return <Box key={code} role="option" aria-selected={exists} sx={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:1,p:1.4,borderBottom:`1px solid ${C.line}`}}>
              <Box><Typography sx={{fontSize:14,fontWeight:800}}>{s.name}<Typography component="span" sx={{ml:1,fontSize:12,color:C.muted}}>{code} · {s.market||'시장 확인 중'}</Typography></Typography></Box>
              <Button size="small" disabled={Boolean(mutating)||exists} variant={exists?'outlined':'contained'} onClick={()=>void add(code)} startIcon={mutating===code?<CircularProgress size={14}/>:<AddRoundedIcon/>}
                sx={{textTransform:'none',fontWeight:800,whiteSpace:'nowrap',bgcolor:exists?undefined:C.blue}}>{exists?'등록됨':'감시 추가'}</Button>
            </Box>;
          })}
        </Box>}
        {feedback&&<Alert severity={/확인했습니다|해제했습니다/.test(feedback)?'success':'warning'} sx={{mt:1.5}}>{feedback}</Alert>}
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{mt:3,mb:1.6}}>
        {([['ALL','전체'],['WATCH','관심'],['HOLDING','보유 확인됨']] as const).map(([key,label])=><Button key={key} onClick={()=>{setMode(key);trackUxEvent('watch_segment_select',{segment:key});}}
          variant={mode===key?'contained':'outlined'} sx={{minHeight:40,textTransform:'none',borderRadius:2,fontWeight:800,
            bgcolor:mode===key?C.navy:undefined,borderColor:C.line,color:mode===key?'#fff':C.ink}}>{label}</Button>)}
      </Stack>
      {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}
      <Box sx={{border:`1px solid ${C.line}`,borderRadius:3,bgcolor:C.white,overflow:'hidden'}}>
        {loading&&!loaded?<Box sx={{p:5,textAlign:'center'}}><CircularProgress/><Typography sx={{mt:2,color:C.muted}}>등록된 종목을 확인 중입니다.</Typography></Box>:
          visible.length===0?<Box sx={{p:4}}><Typography sx={{fontWeight:820,fontSize:17}}>{error?'목록을 불러올 수 없습니다.':mode==='HOLDING'?'보유 여부가 확인된 종목은 없습니다.':'표시할 감시종목이 없습니다.'}</Typography>
          <Typography sx={{mt:1,fontSize:13.5,color:C.muted}}>위 검색에서 종목을 등록하거나 다른 분류를 선택해 주세요.</Typography></Box>:
          visible.map((item,i)=><React.Fragment key={item.ticker}>{i>0&&<Divider/>}
            <Box sx={{p:{xs:2,md:2.8}}}>
              <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" gap={1}>
                <Box><Stack direction="row" alignItems="center" gap={1} flexWrap="wrap"><Typography sx={{fontSize:19,fontWeight:850}}>{item.name}</Typography>
                  <Typography sx={{fontSize:12,color:C.muted}}>{item.ticker}</Typography><Chip variant="outlined" size="small" label={item.decision} sx={{color:shade(item.severity),borderColor:shade(item.severity),fontWeight:700}}/></Stack>
                  <Typography sx={{fontSize:12,color:C.muted,mt:.5}}>{item.asOf} · {item.isHolding?'보유 여부 확인됨':'관심·감시종목'}</Typography></Box>
                <Box sx={{textAlign:{xs:'left',sm:'right'}}}><Typography sx={{fontSize:18,fontWeight:800}}>{money(item.price)}</Typography><Typography sx={{fontSize:13,color:item.rate===null?C.unknown:item.rate>=0?C.green:C.red}}>{pct(item.rate)}</Typography></Box>
              </Stack>
              <Box sx={{mt:2,display:'grid',gridTemplateColumns:{xs:'1fr',md:'1fr 1fr'},gap:2}}>
                <Box><Typography sx={{fontSize:12,fontWeight:800,color:C.muted}}>현재 판단 근거</Typography><Typography sx={{mt:.5,fontSize:14,lineHeight:1.75}}>{item.reason}</Typography></Box>
                <Box><Typography sx={{fontSize:12,fontWeight:800,color:C.muted}}>다음 확인</Typography><Typography sx={{mt:.5,fontSize:14,lineHeight:1.75}}>{item.next}</Typography></Box>
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt:1.6}}>
                <Button endIcon={<ArrowForwardRoundedIcon/>} sx={{fontWeight:820,textTransform:'none'}} onClick={()=>{trackUxEvent('watch_stock_open',{stock_code:item.ticker,source:'watch-workspace'});navigate(`/stock/${item.ticker}`);}}>판단·변화·근거 보기</Button>
                <IconButton aria-label={`${item.name} 감시 해제`} color="default" disabled={Boolean(mutating)} onClick={()=>void remove(item.ticker,item.name)}><DeleteOutlineRoundedIcon/></IconButton>
              </Stack>
            </Box>
          </React.Fragment>)}
      </Box>
      <Box sx={{bgcolor:C.white,border:`1px solid ${C.line}`,borderRadius:3,p:3,mt:2.5}}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography component="h2" sx={{fontSize:19,fontWeight:830}}>내 종목에서 달라진 것</Typography>
          <Button startIcon={<NotificationsActiveRoundedIcon/>} onClick={()=>navigate('/mypage?tab=alerts')} sx={{textTransform:'none',fontWeight:750}}>알림 설정</Button>
        </Stack>
        {trackedChanges.length===0?<Typography sx={{mt:2,color:C.muted,fontSize:13.5}}>연결된 변화가 없거나 아직 조회되지 않았습니다. 위험이 없다는 의미는 아닙니다.</Typography>:
          trackedChanges.slice(0,8).map((event,i)=><Box key={first(event.id,event.changeId,`${changeTicker(event)}-${i}`)} sx={{py:1.8,borderTop:i?`1px solid ${C.line}`:'none'}}>
            <Typography sx={{fontWeight:800,fontSize:14}}>{first(event.title,event.message,event.eventType,'변화 감지')}</Typography>
            <Typography sx={{color:C.muted,fontSize:12,mt:.6}}>{changeTicker(event)} · {first(event.reason,event.summary,'확인 가능한 이유를 검증 중입니다.')}</Typography>
          </Box>)}
      </Box>
      <Typography sx={{fontSize:12,color:C.unknown,mt:2,lineHeight:1.7}}>가격 변화와 기업가치 변화는 별개입니다. 데이터가 부족할 때는 임의의 상태나 원인을 생성하지 않습니다.</Typography>
    </Box>
  </Box>;
}
